import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  DocumentCheckIcon,
  QrCodeIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { Scanner } from '@yudiel/react-qr-scanner';
import toast from 'react-hot-toast';
import { invoiceApi } from '../../api/invoiceApi';
import { customerApi } from '../../api/customerApi';
import { productApi } from '../../api/productApi';
import { generateTaxInvoice } from '../../utils/generateInvoice';
import { useAuth } from '../../context/AuthContext';

const CreateInvoice = () => {
  const { id, invoiceId } = useParams(); // id = customerId (optional), invoiceId = invoice for editing
  const navigate = useNavigate();

  const [customer, setCustomer] = useState(null);
  const [allCustomers, setAllCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(id || '');
  
  // Walk-in Customer State
  const [isWalkIn, setIsWalkIn] = useState(false);
  const [walkInDetails, setWalkInDetails] = useState({ name: '', phone: '', address: '' });

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  
  const [isEditMode, setIsEditMode] = useState(false);

  const { activeBusiness } = useAuth();

  const [items, setItems] = useState([
    { id: Date.now(), productId: '', name: '', hsnCode: '', quantity: 1, unitPrice: 0, total: 0 }
  ]);
  const [includeGst, setIncludeGst] = useState(false);
  const [gstRate, setGstRate] = useState(18); // Default
  const [taxAmount, setTaxAmount] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [advanceAmount, setAdvanceAmount] = useState(0);
  const [termsAndConditions, setTermsAndConditions] = useState(
    "• Please pay within the due date to avoid late fees.\n• Intellectual Property: Assets remain property of business until full payment.\n• Governance: Disputes subject to local jurisdiction."
  );
  const [linkToLedger, setLinkToLedger] = useState(true);

  useEffect(() => {
    if (invoiceId) {
      setIsEditMode(true);
      fetchInvoice(invoiceId);
    } else if (id) {
      fetchCustomer(id);
    } else {
      fetchAllCustomers();
    }
    fetchProducts();
    if (activeBusiness?.settings?.gstRate) {
      setGstRate(activeBusiness.settings.gstRate);
    }
  }, [id, activeBusiness]);

  const fetchAllCustomers = async () => {
    try {
      const res = await customerApi.getCustomers();
      setAllCustomers(res.data.data || []);
    } catch (e) {
      toast.error('Failed to load customers');
    }
  };

  const fetchCustomer = async (customerId) => {
    try {
      const res = await customerApi.getCustomerDetails(customerId);
      setCustomer(res.data.data);
    } catch (error) {
      toast.error('Failed to load customer details');
    }
  };

  const handleCustomerSelect = (e) => {
    const custId = e.target.value;
    setSelectedCustomerId(custId);
    if (custId) {
      fetchCustomer(custId);
    } else {
      setCustomer(null);
    }
  };

  const fetchInvoice = async (invId) => {
    try {
      setLoading(true);
      const res = await invoiceApi.getInvoiceDetails(invId);
      const invoice = res.data.data;
      
      if (invoice.customerId) {
        setSelectedCustomerId(invoice.customerId._id);
        setCustomer(invoice.customerId);
      } else if (invoice.walkInCustomer) {
        setIsWalkIn(true);
        setWalkInDetails(invoice.walkInCustomer);
      }
      
      const formattedItems = invoice.items.map(item => ({
        id: Date.now() + Math.random(),
        productId: item.productId || '',
        name: item.name,
        hsnCode: item.hsnCode || '',
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        total: item.total
      }));
      setItems(formattedItems);
      
      setTaxAmount(invoice.taxAmount);
      setDiscountAmount(invoice.discountAmount);
      setAdvanceAmount(invoice.paidAmount);
      setTermsAndConditions(invoice.termsAndConditions || termsAndConditions);
      setIncludeGst(invoice.taxAmount > 0);
      
      setLoading(false);
    } catch (e) {
      toast.error('Failed to load invoice');
      setLoading(false);
    }
  };

  const handleWalkInPhoneChange = async (e) => {
    const phone = e.target.value;
    setWalkInDetails(prev => ({ ...prev, phone }));

    if (phone.length === 10) {
      try {
        // First check in saved customers
        const existingCustomer = allCustomers.find(c => c.phone === phone);
        if (existingCustomer) {
          setWalkInDetails(prev => ({
            ...prev,
            name: existingCustomer.name,
            address: existingCustomer.address || existingCustomer.address?.street || ''
          }));
          toast.success('Auto-filled from saved customer');
          return;
        }

        // If not found in saved customers, check past walk-in invoices
        const res = await invoiceApi.getWalkInCustomerByPhone(phone);
        if (res.data?.data) {
          setWalkInDetails(prev => ({
            ...prev,
            name: res.data.data.name,
            address: res.data.data.address || ''
          }));
          toast.success('Auto-filled from past records');
        }
      } catch (error) {
        // It's a new walk-in phone number, no action needed
      }
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await productApi.getProducts();
      setProducts(res.data.data || []);
    } catch (error) {
      console.error('Failed to load products');
    }
  };

  const addItem = () => {
    setItems([
      ...items,
      { id: Date.now(), productId: '', name: '', hsnCode: '', imageUrl: '', quantity: 1, unitPrice: 0, total: 0 }
    ]);
  };

  const removeItem = (idToRemove) => {
    if (items.length === 1) return;
    setItems(items.filter(item => item.id !== idToRemove));
  };

  const handleItemChange = (idToUpdate, field, value) => {
    setItems(items.map(item => {
      if (item.id === idToUpdate) {
        const updatedItem = { ...item, [field]: value };
        
        // Auto-fill from product selection
        if (field === 'productId') {
          const selectedProduct = products.find(p => p._id === value);
          if (selectedProduct) {
            updatedItem.name = selectedProduct.name;
            updatedItem.unitPrice = selectedProduct.price;
            updatedItem.hsnCode = selectedProduct.hsnCode || '';
            updatedItem.imageUrl = selectedProduct.imageUrl || '';
          }
        }
        
        // Recalculate item total
        updatedItem.total = (Number(updatedItem.quantity) || 0) * (Number(updatedItem.unitPrice) || 0);
        return updatedItem;
      }
      return item;
    }));
  };

  // Calculations
  const subtotal = items.reduce((sum, item) => sum + item.total, 0);

  const processBarcode = (code) => {
    if (!code || !code.trim()) return;
    const matchedProduct = products.find(p => p.barcode && p.barcode === code.trim());
    if (matchedProduct) {
      // Check if product already in items
      const existingItemIndex = items.findIndex(i => i.productId === matchedProduct._id);
      if (existingItemIndex > -1) {
        // Increment quantity
        const newItems = [...items];
        newItems[existingItemIndex].quantity = Number(newItems[existingItemIndex].quantity) + 1;
        newItems[existingItemIndex].total = newItems[existingItemIndex].quantity * newItems[existingItemIndex].unitPrice;
        setItems(newItems);
        toast.success(`Increased quantity of ${matchedProduct.name}`);
      } else {
        // Add new item
        const emptyRowIndex = items.findIndex(i => !i.productId && !i.name);
        
        const newItem = {
          id: Date.now() + Math.random(),
          productId: matchedProduct._id,
          name: matchedProduct.name,
          hsnCode: matchedProduct.hsnCode || '',
          imageUrl: matchedProduct.imageUrl || '',
          quantity: 1,
          unitPrice: matchedProduct.price,
          total: matchedProduct.price
        };

        if (emptyRowIndex > -1) {
          const newItems = [...items];
          newItems[emptyRowIndex] = newItem;
          setItems(newItems);
        } else {
          setItems([...items, newItem]);
        }
        toast.success(`Added ${matchedProduct.name}`);
      }
    } else {
      toast.error('No product found with this barcode');
    }
  };

  const handleBarcodeScan = (e) => {
    e.preventDefault();
    processBarcode(barcodeInput);
    setBarcodeInput('');
  };

  const onCameraScan = (result) => {
    if (result && result.length > 0) {
      const code = result[0].rawValue;
      if (code) {
        processBarcode(code);
        setShowScanner(false);
      }
    }
  };
  
  // Recalculate tax if GST is included
  useEffect(() => {
    if (includeGst) {
      const calculatedTax = (subtotal * gstRate) / 100;
      setTaxAmount(calculatedTax);
    } else {
      setTaxAmount(0);
    }
  }, [subtotal, includeGst, gstRate]);

  const grandTotal = subtotal + Number(taxAmount) - Number(discountAmount);
  const balanceDue = Math.max(0, grandTotal - Number(advanceAmount));

  const handleSave = async () => {
    if (!isWalkIn && !selectedCustomerId && !id) {
      toast.error('Please select a customer first');
      return;
    }
    if (isWalkIn && !walkInDetails.name) {
      toast.error('Walk-in customer name is required');
      return;
    }

    // Validate
    const validItems = items.filter(i => i.name.trim() !== '' && i.quantity > 0 && i.unitPrice > 0);
    if (validItems.length === 0) {
      toast.error('Please add at least one valid item');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        customerId: isWalkIn ? undefined : (id || selectedCustomerId),
        walkInCustomer: isWalkIn ? walkInDetails : undefined,
        type: 'sale',
        items: validItems,
        taxAmount: Number(taxAmount),
        discountAmount: Number(discountAmount),
        paidAmount: Number(advanceAmount),
        balanceDue: balanceDue,
        termsAndConditions: termsAndConditions,
        linkToLedger: isWalkIn ? false : linkToLedger, // Don't link if walk-in
        issueDate: new Date()
      };

      let res;
      if (isEditMode) {
        res = await invoiceApi.updateInvoice(invoiceId, payload);
      } else {
        res = await invoiceApi.createInvoice(payload);
      }
      
      // Also generate the PDF right now
      generateTaxInvoice(
        {
          invoiceNumber: res.data?.data?.invoiceNumber,
          items: validItems,
          subtotal: subtotal,
          taxAmount: taxAmount,
          discountAmount: discountAmount,
          advanceAmount: advanceAmount,
          balanceDue: balanceDue,
          grandTotal: grandTotal,
          termsAndConditions: termsAndConditions
        }, 
        activeBusiness, 
        isWalkIn ? walkInDetails : customer, 
        includeGst, 
        gstRate
      );

      toast.success(isEditMode ? 'Bill Updated & Downloaded Successfully!' : 'Bill Created & Downloaded Successfully!');
      if (id) {
        navigate(`/customers/${id}/ledger`);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create bill');
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto min-h-[calc(100vh-80px)] flex flex-col bg-white dark:bg-gray-800 md:rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800">
      
      {/* Header */}
      <div className="flex items-center gap-4 p-4 md:p-6 border-b border-gray-100 dark:border-gray-700">
        <Link 
          to={id ? `/customers/${id}/ledger` : '/dashboard'}
          className="p-2 -ml-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <ArrowLeftIcon className="h-6 w-6 text-gray-700 dark:text-gray-300" />
        </Link>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">
            {isEditMode ? 'Update Bill / Invoice' : 'Create Bill / Invoice'}
          </h1>
          {!id && !isEditMode ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Generate a universal invoice</p>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              For {customer?.name || 'Loading...'}
            </p>
          )}
        </div>
      </div>

      {/* Main Form Area */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-8">
        
        {/* Customer Selector / Walk-in Toggle */}
        {!id && (
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-xl border border-red-100 dark:border-red-800/30 space-y-4">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-bold text-red-900 dark:text-red-100">Customer Details</label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={isWalkIn} onChange={(e) => setIsWalkIn(e.target.checked)} className="rounded text-[#dc343b] border-red-300 focus:ring-[#dc343b] w-4 h-4 cursor-pointer" />
                <span className="text-sm font-semibold text-red-800 dark:text-red-200">Walk-in Customer (No Ledger)</span>
              </label>
            </div>
            
            {!isWalkIn ? (
              <select
                value={selectedCustomerId}
                onChange={handleCustomerSelect}
                className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-[#dc343b] focus:border-[#dc343b] block p-3"
              >
                <option value="">-- Choose a Saved Customer --</option>
                {allCustomers.map(c => (
                  <option key={c._id} value={c._id}>{c.name} ({c.phone})</option>
                ))}
              </select>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <input 
                  type="text" 
                  placeholder="Customer Name *" 
                  value={walkInDetails.name}
                  onChange={(e) => setWalkInDetails({...walkInDetails, name: e.target.value})}
                  className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white text-sm rounded-lg p-3 focus:ring-[#dc343b] focus:border-[#dc343b]"
                />
                <input 
                  type="text" 
                  placeholder="Phone Number (Optional)" 
                  value={walkInDetails.phone}
                  onChange={handleWalkInPhoneChange}
                  className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white text-sm rounded-lg p-3 focus:ring-[#dc343b] focus:border-[#dc343b]"
                />
                <input 
                  type="text" 
                  placeholder="Address (Optional)" 
                  value={walkInDetails.address}
                  onChange={(e) => setWalkInDetails({...walkInDetails, address: e.target.value})}
                  className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white text-sm rounded-lg p-3 focus:ring-[#dc343b] focus:border-[#dc343b]"
                />
              </div>
            )}
          </div>
        )}

        {/* Items List */}
        <div className={`space-y-4 ${(!id && !customer && !isWalkIn) ? 'opacity-50 pointer-events-none' : ''}`}>
          
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between pb-4 border-b border-gray-200 dark:border-gray-700 gap-4">
            <h2 className="text-lg font-bold text-gray-800 dark:text-white">Items</h2>
            
            <div className="flex items-center gap-4 w-full sm:w-auto">
              {/* Barcode Scanner Input */}
              <div className="flex flex-1 sm:w-64 gap-2">
                <form onSubmit={handleBarcodeScan} className="flex-1">
                  <input 
                    type="text" 
                    placeholder="Scan Barcode & Press Enter..." 
                    value={barcodeInput}
                    onChange={(e) => setBarcodeInput(e.target.value)}
                    className="w-full bg-white dark:bg-gray-900 border border-red-200 dark:border-red-700/50 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-[#dc343b] focus:border-[#dc343b] block p-2.5 shadow-sm"
                    autoFocus
                  />
                </form>
                <button
                  onClick={() => setShowScanner(true)}
                  className="p-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 transition-colors shadow-sm"
                  title="Scan with Camera"
                >
                  <QrCodeIcon className="w-5 h-5" />
                </button>
              </div>
              
              <button 
                onClick={addItem}
                className="flex items-center gap-2 text-sm font-semibold text-[#dc343b] hover:text-[#b91c1c] dark:text-red-400 dark:hover:text-red-300 whitespace-nowrap"
              >
                <PlusIcon className="w-4 h-4" /> Add Row
              </button>
            </div>
          </div>

          {items.map((item, index) => (
            <div key={item.id} className="grid grid-cols-1 md:grid-cols-[1.5fr_2fr_0.8fr_0.8fr_1fr_auto] gap-4 items-end bg-gray-50 dark:bg-gray-900/50 p-4 rounded-xl border border-gray-100 dark:border-gray-800 relative group">
              
              <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Product Dropdown (Optional) */}
                <div>
                  <label className="block text-[11px] font-bold text-gray-500 mb-1 uppercase tracking-wider">Product (Optional)</label>
                  <select
                    value={item.productId}
                    onChange={(e) => handleItemChange(item.id, 'productId', e.target.value)}
                    className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-[#dc343b] focus:border-[#dc343b] block p-2.5"
                  >
                    <option value="">-- Custom --</option>
                    {products.map(p => (
                      <option key={p._id} value={p._id}>{p.name} (₹{p.price})</option>
                    ))}
                  </select>
                </div>
                
                {/* Item Name */}
                <div>
                  <label className="block text-[11px] font-bold text-gray-500 mb-1 uppercase tracking-wider">Item Details</label>
                  <input
                    type="text"
                    value={item.name}
                    onChange={(e) => handleItemChange(item.id, 'name', e.target.value)}
                    placeholder="e.g. Service or Product Name"
                    className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-[#dc343b] focus:border-[#dc343b] block p-2.5"
                  />
                </div>
              </div>

              {/* HSN Code */}
              <div>
                <label className="block text-[11px] font-bold text-gray-500 mb-1 uppercase tracking-wider">HSN/SAC (Optional)</label>
                <input
                  type="text"
                  value={item.hsnCode || ''}
                  onChange={(e) => handleItemChange(item.id, 'hsnCode', e.target.value)}
                  placeholder="HSN"
                  className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-[#dc343b] focus:border-[#dc343b] block p-2.5"
                />
              </div>

              {/* Quantity */}
              <div>
                <label className="block text-[11px] font-bold text-gray-500 mb-1 uppercase tracking-wider">Qty</label>
                <input
                  type="number"
                  min="1"
                  value={item.quantity}
                  onChange={(e) => handleItemChange(item.id, 'quantity', e.target.value)}
                  className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-[#dc343b] focus:border-[#dc343b] block p-2.5"
                />
              </div>

              {/* Price */}
              <div>
                <label className="block text-[11px] font-bold text-gray-500 mb-1 uppercase tracking-wider">Price (₹)</label>
                <input
                  type="number"
                  min="0"
                  value={item.unitPrice}
                  onChange={(e) => handleItemChange(item.id, 'unitPrice', e.target.value)}
                  className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-[#dc343b] focus:border-[#dc343b] block p-2.5"
                />
              </div>

              {/* Total & Remove */}
              <div className="flex items-center justify-between md:justify-end gap-4 h-[42px]">
                <div className="text-right">
                  <span className="block text-[11px] font-bold text-gray-500 mb-1 md:hidden uppercase tracking-wider">Total</span>
                  <span className="font-bold text-gray-900 dark:text-white">₹{item.total}</span>
                </div>
                {items.length > 1 && (
                  <button 
                    onClick={() => removeItem(item.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 p-2 rounded-lg transition-colors"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                )}
              </div>

            </div>
          ))}
        </div>

        {/* Calculations Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          
          {/* Left Settings */}
          <div className="space-y-4">
            <label className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-xl cursor-pointer">
              <input
                type="checkbox"
                checked={linkToLedger}
                onChange={(e) => setLinkToLedger(e.target.checked)}
                className="w-5 h-5 text-[#dc343b] bg-white border-gray-300 rounded focus:ring-[#dc343b] dark:bg-gray-800 dark:border-gray-600 cursor-pointer"
              />
              <div>
                <p className="font-bold text-gray-900 dark:text-gray-100">Add to Customer's Khata Balance</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">This will automatically create a "You Gave" transaction.</p>
              </div>
            </label>

            <label className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-xl cursor-pointer">
              <input
                type="checkbox"
                checked={includeGst}
                onChange={(e) => setIncludeGst(e.target.checked)}
                className="w-5 h-5 text-[#dc343b] bg-white border-gray-300 rounded focus:ring-[#dc343b] dark:bg-gray-800 dark:border-gray-600 cursor-pointer"
              />
              <div>
                <p className="font-bold text-gray-900 dark:text-gray-100">Include GST @ {gstRate}%</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Auto-calculates CGST & SGST based on subtotal.</p>
              </div>
            </label>

            {/* Terms and Conditions Input */}
            <div className="p-4 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-xl">
              <label className="block text-sm font-bold text-gray-900 dark:text-gray-100 mb-2">Official Service Terms</label>
              <textarea
                value={termsAndConditions}
                onChange={(e) => setTermsAndConditions(e.target.value)}
                rows={4}
                className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-[#dc343b] focus:border-[#dc343b] block p-2.5 resize-none"
                placeholder="Enter your custom terms and conditions here..."
              ></textarea>
            </div>
          </div>

          {/* Right Totals */}
          <div className="bg-gray-50 dark:bg-gray-900/50 p-6 rounded-2xl border border-gray-100 dark:border-gray-800 space-y-4">
            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 font-medium">
              <span>Subtotal</span>
              <span>₹{subtotal}</span>
            </div>
            
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">Tax Amount {includeGst ? `(${gstRate}%)` : '(+)'}</span>
              <div className="relative w-32">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                <input
                  type="number"
                  value={taxAmount}
                  onChange={(e) => setTaxAmount(e.target.value)}
                  className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-[#dc343b] focus:border-[#dc343b] block pl-8 p-2 text-right"
                />
              </div>
            </div>

            <div className="flex items-center justify-between gap-4">
              <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">Discount (-)</span>
              <div className="relative w-32">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                <input
                  type="number"
                  value={discountAmount}
                  onChange={(e) => setDiscountAmount(e.target.value)}
                  className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-white text-sm rounded-lg focus:ring-[#dc343b] focus:border-[#dc343b] block pl-8 p-2 text-right"
                />
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <span className="text-lg font-bold text-gray-900 dark:text-white">Grand Total</span>
              <span className="text-xl font-extrabold text-gray-900 dark:text-white">₹{grandTotal}</span>
            </div>

            <div className="flex items-center justify-between gap-4 pt-2">
              <span className="text-sm text-gray-600 dark:text-gray-400 font-bold">Advance Paid (-)</span>
              <div className="relative w-32">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                <input
                  type="number"
                  value={advanceAmount}
                  onChange={(e) => setAdvanceAmount(e.target.value)}
                  className="w-full bg-white dark:bg-gray-800 border border-[#dc343b] dark:border-[#dc343b] text-[#dc343b] font-bold text-sm rounded-lg focus:ring-[#dc343b] focus:border-[#dc343b] block pl-8 p-2 text-right"
                />
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <span className="text-lg font-bold text-[#dc343b] dark:text-red-400">Balance Due</span>
              <span className="text-2xl font-extrabold text-[#dc343b] dark:text-red-400">₹{balanceDue}</span>
            </div>
          </div>
        </div>

      </div>

      {/* Footer */}
      <div className="p-4 md:p-6 border-t border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
        <button 
          onClick={handleSave}
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 bg-[#dc343b] hover:bg-[#b91c1c] text-white py-4 rounded-xl font-bold text-[15px] transition-colors uppercase tracking-widest disabled:opacity-70 shadow-sm shadow-red-500/20"
        >
          <DocumentCheckIcon className="w-6 h-6" />
          {loading ? 'Processing...' : (isEditMode ? 'Update Bill' : 'Create Bill')}
        </button>
      </div>

      {/* QR Scanner Modal */}
      {showScanner && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 w-full max-w-md rounded-2xl shadow-xl overflow-hidden flex flex-col h-[500px]">
            <div className="flex justify-between items-center p-4 border-b border-gray-100 dark:border-gray-700">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <QrCodeIcon className="w-5 h-5 text-[#dc343b]" />
                Scan Product Barcode
              </h3>
              <button 
                onClick={() => setShowScanner(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 bg-black relative">
              <Scanner 
                onScan={onCameraScan} 
                formats={['qr_code', 'ean_13', 'code_128', 'code_39', 'upc_a', 'upc_e']}
              />
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CreateInvoice;
